import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import api from "@/lib/api"

export interface NotificationLogItem {
  id: string
  notification_type: string
  channel: string
  recipient_email?: string
  recipient_name?: string
  recipient_user_id?: string
  subject?: string
  status: string
  error_message?: string
  sent_by?: string
  sent_at?: string
  created_at?: string
}

export interface NotificationLogDetail extends NotificationLogItem {
  body_html?: string
  body_text?: string
  variables?: string
}

interface PaginatedLogs {
  items: NotificationLogItem[]
  total: number
  page: number
  page_size: number
  pages: number
}

interface LogFilters {
  page?: number
  page_size?: number
  notification_type?: string
  channel?: string
  status?: string
  recipient?: string
  search?: string
}

export function useNotificationLogs(filters: LogFilters = {}) {
  const params = new URLSearchParams()
  if (filters.page) params.set("page", String(filters.page))
  if (filters.page_size) params.set("page_size", String(filters.page_size))
  if (filters.notification_type) params.set("notification_type", filters.notification_type)
  if (filters.channel) params.set("channel", filters.channel)
  if (filters.status) params.set("status", filters.status)
  if (filters.recipient) params.set("recipient", filters.recipient)
  if (filters.search) params.set("search", filters.search)

  return useQuery<PaginatedLogs>({
    queryKey: ["notification-logs", filters],
    queryFn: async () => {
      const { data } = await api.get(`/notification-logs/?${params.toString()}`)
      return data
    },
  })
}

export function useNotificationLogDetail(logId: string | null) {
  return useQuery<NotificationLogDetail>({
    queryKey: ["notification-log", logId],
    queryFn: async () => {
      const { data } = await api.get(`/notification-logs/${logId}`)
      return data
    },
    enabled: !!logId,
  })
}

export function useResendNotification() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (logId: string) => {
      const { data } = await api.post(`/notification-logs/${logId}/resend`)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notification-logs"] })
    },
  })
}
